import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'react-native';
import zipcodes from 'zipcodes-nrviens';
import { useLinkTo } from '@react-navigation/native';

// API Hooks
import {
  useLazyVerifyUserWithJwtQuery,
  // useRegisterUserMutation,
} from 'src/features/authentication/api/authenticationApi';
import {
  useLazyGetCompanyByDotMcQuery,
  useLazyGetCompanyQuery,
  // useUpdateCompanyMutation,
  // useCreateCompanyMutation,
} from 'src/features/company/api/companyApi';
import { useLazyGetAllDriversQuery } from 'src/features/drivers/api/driverApi';
import { useLazyGetAllVehiclesQuery } from 'src/features/vehicles/api/vehicleApi';
import { useLazyGetCompanyCommoditiesQuery } from 'src/features/freight/api/freightApi';
import { useLazyGetApplicationByCompanyIdQuery } from 'src/features/application/api/applicationApi';
import { useLazyGetQuotesByCompanyIdQuery } from 'src/features/quotes/api/quotesApi';

// Redux Actions
import {
  setUser,
  setCurrentUser,
  setUserToken,
  setProducerPhoneNumber,
} from 'src/features/user/slice/userSlice';
import { setCompany, clearCompany, resetDirtyFlag } from 'src/features/company/slice/companySlice';
import { clearApplication } from 'src/features/application/slice/applicationSlice';
import { resetLossState } from 'src/features/application/slice/lossSlice';
import { clearCommodity } from 'src/features/freight/slice/freightSlice';
import { clearQoutes } from 'src/features/quotes/slice/quoteSlice';
import { clearAllDrivers } from 'src/features/drivers/slice/driverSlice';
import { clearAllVehicles } from 'src/features/vehicles/slice/vehicleSlice';
import { resetTabs, setActiveTab, setFilledTabsIndex, setTpJwtToken } from 'src/slices/UiSlice';

// Types & Constants
import { Company } from '@typeRoots/common/company';
import { UserWithCompany } from '@typeRoots/common/user';
import { RootState } from 'src/store/store';
import { URLS } from 'src/constants/urls';
import { TABS_INDEX } from 'src/constants/tabs';
import { PRODUCER_PHONE_NUMBER } from 'src/Constants';
import { initialCompanyState } from 'src/features/company/slice/initialState';
import { setIdentityEvent } from 'src/helpers/gtm/gtmEvents';
import { TP_LINKS } from 'src/constants/externalLinks';

export type FlowResult =
  | { type: 'dotNumberInput'; userInfo: UserWithCompany | null }
  | { type: 'companyDetails'; userInfo: UserWithCompany | null }
  | { type: 'companyLanding'; leadScore?: string }
  | { type: 'secondUser'; userInfo: UserWithCompany | null }
  // | { type: 'waitingList' }
  | { type: 'garagingAddress'; userInfo: UserWithCompany | null }
  | { type: 'redirect'; url: string }
  | { type: 'quotesSuccess' }
  | { type: 'insurance' }
  | { type: 'freight' }
  | { type: 'driverBypass' }
  | { type: 'vehicleBypass' }
  | { type: 'error'; message: string };

interface UseTokenFlowReturn {
  initializeFromToken: (token: string) => Promise<FlowResult>;
  isLoading: boolean;
  error: string | null;
  resetReduxState: () => void;
}

/**
 * Custom hook to handle the complete token-to-company flow
 * This replaces the scattered logic from SQ screens and centralizes:
 * 1. verify-jwt
 * 2. from-dot (if applicable)
 * 3. register (if needed)
 * 4. companies (if needed)
 */
export const useTokenFlow = (): UseTokenFlowReturn => {
  const dispatch = useDispatch();
  const linkTo = useLinkTo();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Selectors
  const accountType = useSelector((state: RootState) => state.UI.accountType);
  const insuranceRouteSource = useSelector((state: RootState) => state.UI.insuranceRouteSource);

  // API Hooks
  const [verifyUserWithJwt] = useLazyVerifyUserWithJwtQuery();
  const [getCompanyByDotMc] = useLazyGetCompanyByDotMcQuery();
  const [getCompanyById] = useLazyGetCompanyQuery();
  // const [updateCompany] = useUpdateCompanyMutation();
  // const [registerUser] = useRegisterUserMutation();
  // const [createCompany] = useCreateCompanyMutation();

  // Data fetching hooks
  const [triggerAllVehiclesQuery] = useLazyGetAllVehiclesQuery();
  const [triggerAllDriversQuery] = useLazyGetAllDriversQuery();
  const [triggerCompanyCommodityQuery] = useLazyGetCompanyCommoditiesQuery();
  const [triggerCompanyApplicationQuery] = useLazyGetApplicationByCompanyIdQuery();
  const [triggerCompanyQouteQuery] = useLazyGetQuotesByCompanyIdQuery();

  /**
   * Reset all Redux state to initial values
   */
  const resetReduxState = () => {
    dispatch(clearCompany());
    dispatch(clearApplication());
    dispatch(resetLossState());
    dispatch(clearCommodity());
    dispatch(clearQoutes());
    dispatch(clearAllDrivers());
    dispatch(clearAllVehicles());
    dispatch(setUserToken(''));
    dispatch(setProducerPhoneNumber(PRODUCER_PHONE_NUMBER));
    dispatch(resetTabs());
  };

  /**
   * Add county information to company addresses using zipcode
   */
  const enrichCompanyWithCounty = (company: Company): Company => {
    const mailingZip = company.mailingAddress?.zipCode;
    const garagingZip = company.garagingAddress?.zipCode;

    const mailingCounty = mailingZip ? zipcodes.lookup(mailingZip.substring(0, 5))?.county : '';
    const garagingCounty = garagingZip ? zipcodes.lookup(garagingZip.substring(0, 5))?.county : '';

    return {
      ...company,
      mailingAddress: {
        ...company.mailingAddress,
        county: mailingCounty,
      },
      garagingAddress: {
        ...company.garagingAddress,
        county: garagingCounty,
      },
    };
  };

  /**
   * Ensure company has mailing address (use garaging if missing)
   */
  const ensureMailingAddress = (company: Company): Company => {
    if (!company.mailingAddress || !company.mailingAddress.street?.length) {
      return {
        ...company,
        mailingAddress: company.garagingAddress,
      };
    }
    return company;
  };

  /**
   * Navigate to the appropriate screen based on company flow step
   */
  const navigateToFlowStep = (url: string, tabIndex: number) => {
    dispatch(setActiveTab({ activeTab: tabIndex }));
    dispatch(setFilledTabsIndex({ filledTabsIndex: tabIndex }));
    linkTo(url);
  };

  /**
   * Determine next step based on backend's action and screen fields
   * For continue-session: Uses backend's screen suggestion for optimized loading
   */
  const determineCompanyFlowStep = async (
    company: Company,
    token: string,
    action?: string,
    screen?: string,
  ): Promise<FlowResult> => {
    // Handle POLICY_SOLD status - redirect to CSR24
    if (company.flowStep === 'POLICY_SOLD' && accountType !== 'producer') {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        if (!baseUrl) {
          throw new Error('API base URL not configured');
        }

        const response = await fetch(`${baseUrl}/csr24`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch CSR24 redirect URL');
        }

        const redirectUrl = await response.text();
        const finalUrl = `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}auth_token=Bearer ${token}`;

        return { type: 'redirect', url: finalUrl };
      } catch (err) {
        console.error('Error handling CSR24 redirection:', err);
        return { type: 'error', message: 'Failed to redirect to policy screen' };
      }
    }

    // Set company in Redux
    dispatch(setCompany(company));

    // If backend provides screen suggestion (continue-session), use it for optimized navigation
    if (action === 'continue-session' && screen) {
      // Navigate directly to the suggested screen and load data async
      switch (screen) {
        case 'quotes':
          // Fetch all data for quotes screen
          await Promise.all([
            triggerAllVehiclesQuery(company.id),
            triggerAllDriversQuery(company.id),
            triggerCompanyCommodityQuery(company.id),
            triggerCompanyApplicationQuery(company.id),
            triggerCompanyQouteQuery(company.id),
          ]);
          navigateToFlowStep(URLS.QUOTE.SUCCESS, TABS_INDEX.QUOTES);
          return { type: 'quotesSuccess' };

        case 'application':
          // Fetch application and related data
          Promise.all([
            triggerAllVehiclesQuery(company.id),
            triggerAllDriversQuery(company.id),
            triggerCompanyCommodityQuery(company.id),
          ]);
          await triggerCompanyApplicationQuery(company.id);
          navigateToFlowStep(URLS.APPLICATION.INSURANCE, TABS_INDEX.FINAL_DETAILS);
          return { type: 'insurance' };

        case 'freight':
          // Fetch freight and related data
          Promise.all([triggerAllVehiclesQuery(company.id), triggerAllDriversQuery(company.id)]);
          await triggerCompanyCommodityQuery(company.id);
          navigateToFlowStep(URLS.FREIGHT, TABS_INDEX.FREIGHT);
          return { type: 'freight' };

        case 'driver':
          // Fetch drivers and vehicles
          triggerAllVehiclesQuery(company.id);
          await triggerAllDriversQuery(company.id);
          navigateToFlowStep(URLS.DRIVER.BYPASS, TABS_INDEX.DRIVER);
          return { type: 'driverBypass' };

        case 'vehicle':
          await triggerAllVehiclesQuery(company.id);
          navigateToFlowStep(URLS.VEHICLE.BYPASS, TABS_INDEX.VEHICLE);
          return { type: 'vehicleBypass' };

        case 'company':
        default:
          dispatch(resetTabs());
          return {
            type: 'companyLanding',
            leadScore: company.leadScore,
          };
      }
    }

    // Fallback to old progressive flow if no screen suggestion
    // Handle QUOTE_GENERATED or WAITING status
    if (company.flowStep === 'QUOTE_GENERATED' || company.flowStep === 'WAITING') {
      // Fetch all data
      await Promise.all([
        triggerAllVehiclesQuery(company.id),
        triggerAllDriversQuery(company.id),
        triggerCompanyCommodityQuery(company.id),
        triggerCompanyApplicationQuery(company.id),
        triggerCompanyQouteQuery(company.id),
      ]);

      navigateToFlowStep(URLS.QUOTE.SUCCESS, TABS_INDEX.QUOTES);
      return { type: 'quotesSuccess' };
    }

    // Progressive flow based on what data exists
    const { data: vehicles } = await triggerAllVehiclesQuery(company.id);
    if (vehicles && vehicles.length > 0) {
      const { data: drivers } = await triggerAllDriversQuery(company.id);
      if (drivers && drivers.length > 0) {
        const { data: commodities } = await triggerCompanyCommodityQuery(company.id);
        if (commodities && commodities.length > 0) {
          const { data: application } = await triggerCompanyApplicationQuery(company.id);
          if (application) {
            navigateToFlowStep(URLS.APPLICATION.INSURANCE, TABS_INDEX.FINAL_DETAILS);
            return { type: 'insurance' };
          }
          navigateToFlowStep(URLS.FREIGHT, TABS_INDEX.FREIGHT);
          return { type: 'freight' };
        }
        navigateToFlowStep(URLS.DRIVER.BYPASS, TABS_INDEX.DRIVER);
        return { type: 'driverBypass' };
      }
      navigateToFlowStep(URLS.VEHICLE.BYPASS, TABS_INDEX.VEHICLE);
      return { type: 'vehicleBypass' };
    }

    // No data yet - go to company landing
    dispatch(resetTabs());
    return {
      type: 'companyLanding',
      leadScore: company.leadScore,
    };
  };

  /**
   * Handle company fetched from DOT/MC (MCP or backend)
   */
  const handleFromDotResponse = (
    company: Company | null | undefined,
    dotNumber: string,
    mcNumber: string,
  ): FlowResult => {
    dispatch(resetTabs());

    if (company) {
      let enrichedCompany = ensureMailingAddress(company);
      enrichedCompany = enrichCompanyWithCounty(enrichedCompany);
      dispatch(setCompany(enrichedCompany));

      return {
        type: 'companyLanding',
        leadScore: enrichedCompany.leadScore,
      };
    }

    // No company found - initialize new one
    const newCompany = { ...initialCompanyState, dotNumber, mcNumber } as Company;
    dispatch(setCompany(newCompany));
    return { type: 'companyDetails', userInfo: null };
  };

  /**
   * Main flow orchestration
   * API #1: verify-jwt
   * API #2: from-dot (conditional)
   * API #3: register (conditional)
   * API #4: companies (handled in CompanyLandingScreen)
   */
  const initializeFromToken = async (token: string): Promise<FlowResult> => {
    setIsLoading(true);
    setError(null);

    try {
      // Validate token
      if (!token || token === '') {
        window.location.href = TP_LINKS.TP_INSURANCE_URL;
        return { type: 'error', message: 'No token provided' };
      }

      // Store token in Redux
      dispatch(setTpJwtToken(token));

      // ========================================
      // API #1: VERIFY JWT
      // ========================================
      const { data: userData } = await verifyUserWithJwt(token);

      if (!userData) {
        setError('Failed to verify user token');
        return { type: 'error', message: 'Failed to verify user token' };
      }

      // ========================================
      // FLOW BRANCHING BASED ON USER STATE AND ACTION
      // ========================================

      const action = userData.action;
      const screen = userData.screen;

      // Handle action-based routing from backend
      // if (action === 'waiting-list') {
      //   // User's insurance expiry is more than 45 days away
      //   return { type: 'waitingList' };
      // }

      // Case 1: User already registered with company ID
      if (userData.user?.id && userData.company?.id) {
        // Set user token for authenticated API calls
        const userToken = userData.token || '';
        dispatch(setUserToken(userToken));

        // Set user identity for analytics (existing user)
        setIdentityEvent(
          userData.user.id,
          userData.user.email,
          userData.user.firstName,
          accountType,
        );

        dispatch(setUser(userData.user));
        dispatch(setCurrentUser(userData.user));

        const company = await getCompanyById(userData.company.id).unwrap();
        dispatch(resetDirtyFlag());

        // Determine next step based on company flow and backend suggestions
        return await determineCompanyFlowStep(company, userToken, action, screen);
      }

      // Case 2: User exists but not enabled (second user from same company)
      if (userData.user?.id && !userData.user.enabled) {
        // Set user token for potential future API calls
        const userToken = userData.token || '';
        dispatch(setUserToken(userToken));

        // Set user identity for analytics (second user - disabled)
        setIdentityEvent(
          userData.user.id,
          userData.user.email,
          userData.user.firstName,
          accountType,
        );

        return {
          type: 'secondUser',
          userInfo: userData,
        };
      }

      // Case 3: User registered but no company - need to fetch/create company
      if (userData.user?.id && !userData.company?.id) {
        // Set user token for authenticated API calls
        const userToken = userData.token || '';
        dispatch(setUserToken(userToken));

        // Set user identity for analytics (registered user without company)
        setIdentityEvent(
          userData.user.id,
          userData.user.email,
          userData.user.firstName,
          accountType,
        );

        const dotNumber = userData.company?.dot || '';
        const mcNumber = userData.company?.mc || '';

        if (!dotNumber && !mcNumber) {
          // No DOT/MC - route to input screen or details
          // If user is a producer, skip DOT input and garaging address, go directly to company details
          if (accountType === 'producer' || insuranceRouteSource === 'tp_dot_collection_page') {
            return { type: 'companyDetails', userInfo: userData };
          }
          return { type: 'dotNumberInput', userInfo: userData };
        }

        // ========================================
        // API #2: FROM-DOT (company lookup)
        // ========================================
        const response = await getCompanyByDotMc({
          dotNumber,
          mcNumber,
          tpJwt: token,
        }).unwrap();

        return handleFromDotResponse(response.company, dotNumber, mcNumber);
      }

      // Case 4: New user (no ID) - will need registration in CompanyLandingScreen
      if (!userData.user?.id) {
        // Set user identity for analytics (new user - no ID yet, use 0)
        setIdentityEvent(
          userData.user?.id || 0,
          userData.user?.email || '',
          userData.user?.firstName || '',
          accountType,
        );

        const dotNumber = userData.company?.dot || '';
        const mcNumber = userData.company?.mc || '';

        if (!dotNumber && !mcNumber) {
          // If user is a producer, skip DOT input and garaging address, go directly to company details
          if (accountType === 'producer' || insuranceRouteSource === 'tp_dot_collection_page') {
            return { type: 'companyDetails', userInfo: userData };
          }
          return { type: 'dotNumberInput', userInfo: userData };
        }

        // ========================================
        // API #2: FROM-DOT (company lookup)
        // ========================================
        const response = await getCompanyByDotMc({
          dotNumber,
          mcNumber,
          tpJwt: token,
        }).unwrap();

        return handleFromDotResponse(response.company, dotNumber, mcNumber);
      }

      // Default fallback
      return { type: 'companyLanding' };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      console.error('Token flow error:', err);
      setError(errorMessage);
      Alert.alert('Error', 'Failed to initialize application. Please try again.');
      return { type: 'error', message: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    initializeFromToken,
    isLoading,
    error,
    resetReduxState,
  };
};
